/**
 * Kanban Board Component
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Task } from '@/types';
import { TaskCard } from './TaskCard';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { updateTask } from '@/store/taskSlice';
import { taskAPI } from '@/api/taskAPI';
import { mastersAPI, WorkflowStatus, TaskType, Priority } from '@/api/mastersApi';
import { Plus, Check, X, Pencil } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverEvent,
  DragEndEvent,
  DragStartEvent,
  useDroppable
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AvatarTooltip from '@/components/AvatarTooltip';
import { TaskTypeIcon } from '@/components/TaskTypeIcon';
import { API_BASE_URL } from '@/utils/constants';
import toast from 'react-hot-toast';
const COLOR_SWATCHES = [
  '#6B7280', '#3B82F6', '#8B5CF6', '#10B981',
  '#F59E0B', '#EF4444', '#EC4899', '#F97316',
  '#059669', '#0EA5E9', '#6366F1', '#111827',
];

interface KanbanBoardProps {
  tasks: Task[];
  onTaskClick: (task: Task) => void;
  onTaskUpdate?: () => void;
  projectId?: string;
  projectMembers?: any[];
  taskTypes?: TaskType[];
  priorities?: Priority[];
  onInlineTaskCreate?: (payload: { statusId: string; isFallback: boolean; title: string; taskTypeId?: string }) => Promise<void>;
  isViewerOnly?: boolean;
  onStatusReorder?: (payload: { draggedStatusId: string; newOrder: number; previousStatusId: string | null; afterStatusId: string | null }) => void;
  onAddStatus?: (payload: { name: string; color: string; category: string }) => Promise<void>;
  onStatusRename?: (statusId: string, newName: string) => Promise<void>;
}

const SortableTaskWrapper = ({ task, children }: any) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    data: { type: 'Task', task }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    zIndex: isDragging ? 999 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...attributes} 
      {...listeners} 
      className="cursor-grab active:cursor-grabbing mb-2 touch-none"
      onDragStart={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {children}
    </div>
  );
};

const ColumnDroppable = ({ id, children, isDraggedOver, onDragOver, onDrop }: any) => {
  const { setNodeRef } = useDroppable({ id, data: { type: 'Column', id } });
  return (
    <div
      ref={setNodeRef}
      className="space-y-1 bg-gray-50 pt-2 px-2"
      style={{
        minHeight: '150px',
        height: '100%',
        paddingBottom: isDraggedOver ? '80px' : '8px',
        transition: 'padding-bottom 0.2s ease-in-out'
      }}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {children}
    </div>
  );
};

export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  tasks,
  onTaskClick,
  onTaskUpdate,
  projectId,
  projectMembers,
  taskTypes, priorities,
  onInlineTaskCreate,
  isViewerOnly,
  onStatusReorder,
  onAddStatus,
  onStatusRename
}) => {
  const dispatch = useAppDispatch();
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [draggedOverColumnId, setDraggedOverColumnId] = useState<string | null>(null);
  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
  const [targetColumnId, setTargetColumnId] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<WorkflowStatus[]>([]);
  const [activeBoardTasks, setActiveBoardTasks] = useState<Record<string, Task[]>>({});
  const [activeDragTask, setActiveDragTask] = useState<Task | null>(null);
  const [useFallback, setUseFallback] = useState<boolean>(false);
  const [isAddingStatus, setIsAddingStatus] = useState(false);
  const [newStatusParams, setNewStatusParams] = useState({ name: '', color: '#6B7280', category: '' });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isSubmittingStatus, setIsSubmittingStatus] = useState(false);
  const { projects } = useAppSelector((state) => state.projects);
  const [activeComposer, setActiveComposer] = useState<string | null>(null);
  const [composerValues, setComposerValues] = useState<Record<string, string>>({});
  const [composerLoading, setComposerLoading] = useState<Record<string, boolean>>({});
  const composerRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const composerInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [composerTypeIds, setComposerTypeIds] = useState<Record<string, string | undefined>>({});
  const [composerTypePicker, setComposerTypePicker] = useState<string | null>(null);
  const [editingColumnId, setEditingColumnId] = useState<string | null>(null);
  const [editingColumnName, setEditingColumnName] = useState<string>('');
  const [isSavingColumnName, setIsSavingColumnName] = useState(false);
  const typePickerRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const { isAdmin } = useAppSelector((state) => state.auth);

   
  const resolveTaskTypeIconUrl = (raw?: string | null) => {
    if (!raw) return null;
    const builtInIcons = ['task', 'bug', 'story', 'epic', 'subtask'];
    if (builtInIcons.includes(raw.toLowerCase())) return null;
    try {
      if (raw.startsWith('data:') || raw.startsWith('http://') || raw.startsWith('https://')) return raw;
      const api = new URL(API_BASE_URL);
      const origin = `${api.protocol}//${api.host}`;
      const path = raw.startsWith('/') ? raw : `/${raw}`;
      return origin + path;
    } catch {
      return null;
    }
  };

  const defaultTaskTypeId = useMemo(() => {
    if (!taskTypes || taskTypes.length === 0) return undefined;
    const preferred = taskTypes.find((type) => (type.name || '').toLowerCase() === 'task');
    const fallback = preferred || taskTypes[0];
    return fallback ? String(fallback.id) : undefined;
  }, [taskTypes]);

  const getTaskTypeById = (id?: string) => {
    if (!id || !taskTypes) return undefined;
    return taskTypes.find((type) => String(type.id) === String(id));
  };

  const fetchStatuses = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await mastersAPI.getWorkflowStatusesList(projectId as string);
      if (!data || data.statuses.length === 0) {
        setUseFallback(true);
        setStatuses([]);
      } else {
        setUseFallback(false);
        const sorted = data.statuses.sort((a, b) => (a.order || 0) - (b.order || 0));
        setStatuses(sorted);
      }
    } catch (e) {
      setUseFallback(true);
      setStatuses([]);
    }
  }, [projectId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      await fetchStatuses();
      if (!mounted) return;
      // if any unmount logic is needed, handle it here
    })();
    return () => { mounted = false; };
  }, [fetchStatuses]);

  useEffect(() => {
    if (!activeComposer) return;

    const handleClickOutside = (event: MouseEvent) => {
      const currentRef = composerRefs.current[activeComposer];
      if (!currentRef) return;
      if (!currentRef.contains(event.target as Node)) {
        const value = composerValues[activeComposer]?.trim();
        if (value) {
          void handleSubmitComposer(activeComposer, value);
        } else {
          handleCancelComposer(activeComposer);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeComposer, composerValues]);

  useEffect(() => {
    if (!activeComposer) return;
    const input = composerInputRefs.current[activeComposer];
    if (input) {
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    }
  }, [activeComposer]);

  useEffect(() => {
    if (!composerTypePicker) return;
    const handleOutside = (event: MouseEvent) => {
      const ref = typePickerRefs.current[composerTypePicker];
      if (ref && !ref.contains(event.target as Node)) {
        setComposerTypePicker(null);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [composerTypePicker]);

  const fallbackStatuses: WorkflowStatus[] = useMemo(() => ([
    { id: 'todo', name: 'TO DO', color: undefined, is_initial: true, is_final: false, order: 1 },
    { id: 'requirements', name: 'REQUIREMENTS SIGN-OFF', color: undefined, is_initial: false, is_final: false, order: 2 },
    { id: 'in_progress', name: 'DEV IN PROGRESS', color: undefined, is_initial: false, is_final: false, order: 3 },
    { id: 'completed', name: 'DEV COMPLETED', color: undefined, is_initial: false, is_final: false, order: 4 },
    { id: 'qa', name: 'QA IN PROGRESS', color: undefined, is_initial: false, is_final: false, order: 5 },
  ]), []);

  const effectiveStatuses = useMemo<WorkflowStatus[]>(
    () => (useFallback ? fallbackStatuses : statuses),
    [useFallback, fallbackStatuses, statuses]
  );

  const tasksByStatusId = useMemo(() => {
    const map: Record<string, Task[]> = {};
    effectiveStatuses.forEach(s => { map[s.id] = []; });

    // Helper: map legacy Task.status to a workflow status id
    const mapLegacyStatusToStatusId = (legacy: string): string | null => {
      if (!legacy) return null;
      const val = String(legacy).toLowerCase();
      const matchByName = (needle: string) =>
        effectiveStatuses.find(s => (s.name || '').toLowerCase().includes(needle))?.id || null;
      switch (val) {
        case 'todo':
        case 'backlog':
          return matchByName('to do') || effectiveStatuses[0]?.id || null;
        case 'requirements':
        case 'pending_signoff':
          return matchByName('requirement') || matchByName('sign') || effectiveStatuses[0]?.id || null;
        case 'in_progress':
          return matchByName('progress') || effectiveStatuses[0]?.id || null;
        case 'completed':
        case 'done':
        case 'uat_completed':
        case 'prod_push':
          return matchByName('completed') || matchByName('done') || effectiveStatuses[effectiveStatuses.length - 1]?.id || null;
        case 'qa':
          return matchByName('qa') || effectiveStatuses[0]?.id || null;
        case 'on_hold':
          return matchByName('hold') || effectiveStatuses[0]?.id || null;
        default:
          return effectiveStatuses.find(s => s.is_initial)?.id || effectiveStatuses[0]?.id || null;
      }
    };

    tasks.forEach(t => {
      if (useFallback) {
        const key = (t as any).status || 'todo';
        if (map[key]) {
          map[key].push(t);
        } else {
          // Put unknown statuses into TODO column by default
          const todoId = 'todo';
          (map[todoId] = map[todoId] || []).push(t);
        }
      } else {
        const sid = (t as any).workflow_status_id;
        if (sid && map[sid]) {
          map[sid].push(t);
        } else {
          const derivedId = mapLegacyStatusToStatusId((t as any).status);
          if (derivedId && map[derivedId]) {
            map[derivedId].push(t);
          } else {
            // Final fallback to initial/first column
            const first = effectiveStatuses.find(s => s.is_initial)?.id || effectiveStatuses[0]?.id;
            if (first) (map[first] = map[first] || []).push(t);
          }
        }
      }
    });
    return map;
  }, [tasks, effectiveStatuses, useFallback]);

  useEffect(() => {
    setActiveBoardTasks(tasksByStatusId);
  }, [tasksByStatusId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDndStart = (event: DragStartEvent) => {
    const { active } = event;
    if (active.data.current?.type === 'Task') {
      setActiveDragTask(active.data.current.task);
    }
  };

  const handleDndOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;
    
    const activeId = active.id;
    const overId = over.id;
    
    if (activeId === overId) return;

    const isActiveTask = active.data.current?.type === 'Task';
    const isOverColumn = over.data.current?.type === 'Column';

    if (!isActiveTask) return;

    // Find containers
    const activeContainer = Object.keys(activeBoardTasks).find(key => activeBoardTasks[key].find(t => t.id === activeId));
    const overContainer = isOverColumn ? overId : Object.keys(activeBoardTasks).find(key => activeBoardTasks[key].find(t => t.id === overId));

    if (!activeContainer || !overContainer || activeContainer === overContainer) {
      return;
    }

    setActiveBoardTasks((prev) => {
      const activeItems = prev[activeContainer];
      const overItems = prev[overContainer];

      const activeIndex = activeItems.findIndex(t => t.id === activeId);
      const overIndex = isOverColumn ? overItems.length : overItems.findIndex(t => t.id === overId);

      return {
        ...prev,
        [activeContainer]: [...prev[activeContainer].filter(t => t.id !== activeId)],
        [overContainer]: [
          ...prev[overContainer].slice(0, overIndex),
          activeItems[activeIndex],
          ...prev[overContainer].slice(overIndex, prev[overContainer].length)
        ]
      };
    });
  };

  const handleDndEnd = async (event: DragEndEvent) => {
    setActiveDragTask(null);
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    const isActiveTask = active.data.current?.type === 'Task';
    if (!isActiveTask) return;

    const activeContainer = Object.keys(activeBoardTasks).find(key => activeBoardTasks[key].find(t => t.id === activeId));
    const overContainer = over.data.current?.type === 'Column' ? overId : Object.keys(activeBoardTasks).find(key => activeBoardTasks[key].find(t => t.id === overId));

    if (!activeContainer || !overContainer) return;

    const activeIndex = activeBoardTasks[activeContainer].findIndex(t => t.id === activeId);
    let newIndex = over.data.current?.type === 'Column' ? activeBoardTasks[overContainer].length : activeBoardTasks[overContainer].findIndex(t => t.id === overId);
    
    let nextTasks = { ...activeBoardTasks };

    if (activeContainer === overContainer) {
      if (activeIndex !== newIndex) {
        nextTasks[activeContainer] = arrayMove(nextTasks[activeContainer], activeIndex, newIndex);
        setActiveBoardTasks(nextTasks);
      }
    }

    const task = active.data.current?.task;
    if (!task) return;

    try {
      const newTasks = nextTasks[overContainer];
      const updates = newTasks.map((t: any, index: number) => {
        const expectedOrder = index + 1;
        const isDraggedAndColChanged = (t.id === activeId && activeContainer !== overContainer);
        
        if (t.board_order !== expectedOrder || isDraggedAndColChanged) {
          if (useFallback) {
            return dispatch(updateTask({ id: t.id, data: { status: overContainer as any, board_order: expectedOrder } as any })).unwrap();
          } else {
            return taskAPI.updateTaskStatusAdvanced(t.id, overContainer as string, expectedOrder)
              .catch(() => dispatch(updateTask({ id: t.id, data: { workflow_status_id: overContainer as string, board_order: expectedOrder } as any })).unwrap());
          }
        }
        return Promise.resolve();
      });

      await Promise.all(updates);
      onTaskUpdate?.();
    } catch (error) {
      setActiveBoardTasks(tasksByStatusId);
    }
  };

  const handleColumnDragStart = (e: React.DragEvent, columnId: string) => {
    e.stopPropagation();
    try {
      e.dataTransfer.setData('text/column-id', columnId);
      e.dataTransfer.effectAllowed = 'move';
    } catch (_) { }
    setDraggedColumnId(columnId);
  };

  const handleColumnDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.stopPropagation();
    try { e.dataTransfer.dropEffect = 'move'; } catch (_) { }
    if (draggedColumnId && draggedColumnId !== columnId) {
      setTargetColumnId(columnId);
    }
  };

  const handleColumnDrop = (e: React.DragEvent, dropTargetId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedColumnId || draggedColumnId === dropTargetId) {
      setDraggedColumnId(null);
      setTargetColumnId(null);
      return;
    }

    const sourceIdx = statuses.findIndex(s => s.id === draggedColumnId);
    const targetIdx = statuses.findIndex(s => s.id === dropTargetId);
    
    if (sourceIdx !== -1 && targetIdx !== -1) {
      const newStatuses = [...statuses];
      const [removed] = newStatuses.splice(sourceIdx, 1);
      newStatuses.splice(targetIdx, 0, removed);

      const updatedStatuses = newStatuses.map((s, idx) => ({ ...s, order: idx + 1 }));
      setStatuses(updatedStatuses);

      if (onStatusReorder) {
        const previousStatusId = targetIdx > 0 ? updatedStatuses[targetIdx - 1].id : null;
        const afterStatusId = targetIdx < updatedStatuses.length - 1 ? updatedStatuses[targetIdx + 1].id : null;
        
        onStatusReorder({
          draggedStatusId: draggedColumnId,
          newOrder: targetIdx + 1,
          previousStatusId,
          afterStatusId
        });
      }
    }
    
    setDraggedColumnId(null);
    setTargetColumnId(null);
  };

  const handleColumnDragEnd = (e: React.DragEvent) => {
    e.stopPropagation();
    setDraggedColumnId(null);
    setTargetColumnId(null);
  };

  const handleDragStart = (e: React.DragEvent, task: Task) => {
    try {
      e.dataTransfer.setData('text/plain', String((task as any).id || ''));
      e.dataTransfer.effectAllowed = 'move';
    } catch (_) { }
    setDraggedTask(task);
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
    setDraggedOverColumnId(null);
  };

  const handleDragOver = (e: React.DragEvent, columnId?: string) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch (_) { }
    if (columnId && draggedTask) {
      setDraggedOverColumnId(columnId);
    }
  };

  const handleDrop = async (statusId: string, e?: React.DragEvent) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    let task = draggedTask;
    if (!task && e) {
      try {
        const id = e.dataTransfer.getData('text/plain');
        task = tasks.find(t => String((t as any).id) === id) || null;
      } catch (_) { }
    }
    if (!task) return;

    try {
      const nextOrder = ((tasksByStatusId[statusId] || []).length) + 1;

      if (useFallback) {
        // Classic board using Task.status
        await dispatch(updateTask({ id: task.id, data: { status: statusId as any } })).unwrap();
      } else {
        // Workflow board using workflow_status_id (MongoDB ObjectId format or any string ID)
        try {
          await taskAPI.updateTaskStatusAdvanced(task.id, statusId, nextOrder);
        } catch (apiError: any) {
          // Fallback to generic update if advanced endpoint not available
          await dispatch(
            updateTask({ id: task.id, data: { workflow_status_id: statusId, board_order: nextOrder } as any })
          ).unwrap();
        }
      }

      onTaskUpdate?.();
    } catch (error) {
      // Silent error handling
    }

    setDraggedTask(null);
    setDraggedOverColumnId(null);
  };

  const toggleComposer = (statusId: string) => {
    if (activeComposer === statusId) {
      setActiveComposer(null);
      setComposerTypePicker(null);
      return;
    }
    setActiveComposer(statusId);
    setComposerValues((prev) => ({
      ...prev,
      [statusId]: prev[statusId] ?? ''
    }));
    setComposerTypeIds((prev) => ({
      ...prev,
      [statusId]: prev[statusId] ?? defaultTaskTypeId
    }));
    setComposerTypePicker(null);
  };

  const handleCancelComposer = (statusId: string) => {
    if (activeComposer === statusId) {
      setActiveComposer(null);
      setComposerTypePicker(null);
    }
    setComposerValues((prev) => {
      const next = { ...prev };
      delete next[statusId];
      return next;
    });
    setComposerLoading((prev) => {
      const next = { ...prev };
      delete next[statusId];
      return next;
    });
    setComposerTypeIds((prev) => {
      const next = { ...prev };
      delete next[statusId];
      return next;
    });
  };

  const handleSubmitComposer = async (statusId: string, rawTitle?: string) => {
    if (!onInlineTaskCreate) {
      setActiveComposer(null);
      return;
    }
    const title = (rawTitle ?? composerValues[statusId] ?? '').trim();
    if (!title) {
      handleCancelComposer(statusId);
      return;
    }
    const selectedTypeId = composerTypeIds[statusId] ?? defaultTaskTypeId;
    setComposerLoading((prev) => ({ ...prev, [statusId]: true }));
    try {
      await onInlineTaskCreate({ statusId, isFallback: useFallback, title, taskTypeId: selectedTypeId });
      onTaskUpdate?.();
      handleCancelComposer(statusId);
    } catch (error) {
      console.error('Failed to create inline task', error);
      setComposerLoading((prev) => ({ ...prev, [statusId]: false }));
    }
  };

  const renderComposer = (columnId: string) => {
    if (activeComposer !== columnId) return null;
    const selectedTypeId = composerTypeIds[columnId] ?? defaultTaskTypeId;
    const selectedType = getTaskTypeById(selectedTypeId);
    const selectedIcon = resolveTaskTypeIconUrl(
      (selectedType as any)?.icon ||
      (selectedType as any)?.icon_url ||
      (selectedType as any)?.image ||
      (selectedType as any)?.image_url
    );
    return (
      <div
        ref={(el) => {
          composerRefs.current[columnId] = el;
          typePickerRefs.current[columnId] = el;
        }}
        className="bg-white border border-blue-300 rounded-lg shadow-sm p-3 mb-3"
      >
        <div className="flex items-center gap-2">
          {taskTypes && taskTypes.length > 0 && (
            <div className="relative">
              <AvatarTooltip label={selectedType?.name || 'Task type'} position="bottom">
                <button
                  type="button"
                  onClick={() => setComposerTypePicker((prev) => (prev === columnId ? null : columnId))}
                  className={`w-9 h-9 rounded-full border flex items-center justify-center ${composerTypePicker === columnId ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                    }`}
                >
                  <TaskTypeIcon
                    url={selectedIcon}
                    name={selectedType?.name}
                    className="w-4 h-4"
                  />
                </button>
              </AvatarTooltip>
              {composerTypePicker === columnId && (
                <div className="absolute z-20 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 w-44">
                  <div className="grid grid-cols-4 gap-2">
                    {taskTypes.map((type) => {
                      const iconUrl = resolveTaskTypeIconUrl(
                        (type as any)?.icon ||
                        (type as any)?.icon_url ||
                        (type as any)?.image ||
                        (type as any)?.image_url
                      );
                      const isSelected = String(type.id) === String(selectedTypeId);
                      return (
                        <AvatarTooltip key={type.id} label={type.name} position="bottom">
                          <button
                            type="button"
                            onClick={() => {
                              setComposerTypeIds((prev) => ({ ...prev, [columnId]: String(type.id) }));
                              setComposerTypePicker(null);
                            }}
                            className={`w-8 h-8 rounded-full border flex items-center justify-center transition ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-transparent hover:bg-gray-100'
                              }`}
                          >
                            <TaskTypeIcon url={iconUrl} name={type.name} className="w-4 h-4" />
                          </button>
                        </AvatarTooltip>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          <input
            ref={(el) => { composerInputRefs.current[columnId] = el; }}
            value={composerValues[columnId] ?? ''}
            onChange={(e) =>
              setComposerValues((prev) => ({
                ...prev,
                [columnId]: e.target.value
              }))
            }
            className="flex-1 border border-gray-300 hover:border-blue-300 focus:border-blue-400 focus:outline-none focus:ring-1 text-sm bg-transparent p-1 rounded"
            placeholder="Enter Title of Task..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void handleSubmitComposer(columnId);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                handleCancelComposer(columnId);
              }
            }}
          />
        </div>

        <div className="flex justify-end gap-2 mt-2">
          <AvatarTooltip label={"Create Task"} position="bottom">
            <button
              type="button"
              onClick={() => handleSubmitComposer(columnId)}
              disabled={composerLoading[columnId]}
              className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              title="Create task"
            >
              <Check className="w-4 h-4" />
            </button>
          </AvatarTooltip>
          <AvatarTooltip label={"Cancel"} position="bottom">
            <button
              type="button"
              onClick={() => handleCancelComposer(columnId)}
              className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
              title="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </AvatarTooltip>

        </div>
      </div>
    );
  };

  const extraColsCount = !isViewerOnly && onAddStatus&& isAdmin ? 1 : 0;
  const totalCols = effectiveStatuses.length + extraColsCount;

  const handleSaveColumnName = async (columnId: string) => {
    const trimmed = editingColumnName.trim();
    if (!trimmed || trimmed === effectiveStatuses.find(s => s.id === columnId)?.name) {
      setEditingColumnId(null);
      setEditingColumnName('');
      return;
    }
    if (!onStatusRename) return;
    setIsSavingColumnName(true);
    try {
      await onStatusRename(columnId, trimmed);
      await fetchStatuses();
      toast.success('Status renamed successfully');
      setEditingColumnId(null);
      setEditingColumnName('');
    } catch (e: any) {
      const message = e?.response?.data?.message || e?.message || 'Failed to rename status';
      toast.error(message);
    } finally {
      setIsSavingColumnName(false);
    }
  };

  return (
    <div className="pb-2 px-2 bg-white" style={{ width: 'max-content', minWidth: '100%', height: 'auto' }}>
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDndStart} onDragOver={handleDndOver} onDragEnd={handleDndEnd}>
      <div
        className="flex gap-6 items-stretch"
        style={{
          width: `${totalCols * 320 + Math.max(totalCols - 1, 0) * 24}px`,
          minWidth: '100%',
          height: 'auto',
          marginBottom:"10px",
          paddingBottom:"10px"
        }}
      >
        {effectiveStatuses.map((column) => {
          const isDraggedOver = draggedOverColumnId === column.id && draggedTask !== null;
          return (
            <div
              key={column.id}
              className={`bg-white rounded-lg w-80 flex-shrink-0 flex flex-col border transition-all duration-200 ${
                draggedColumnId === column.id ? 'opacity-40 border-dashed border-gray-400' : 
                targetColumnId === column.id ? 'border-2 border-blue-500 scale-[1.02] shadow-lg' : 'border-gray-200'
              }`}
              style={{ height: 'auto', minHeight: 'auto' }}
              draggable={!isViewerOnly}
              onDragStart={(e) => handleColumnDragStart(e, column.id)}
              onDragEnd={handleColumnDragEnd}
              onDragOver={(e) => {
                if (draggedColumnId) {
                  handleColumnDragOver(e, column.id);
                } else {
                  handleDragOver(e, column.id);
                }
              }}
              onDragLeave={(e) => {
                // Only clear if leaving the column itself, not child elements
                if (e.currentTarget === e.target) {
                  if (draggedColumnId) {
                    setTargetColumnId(null);
                  } else {
                    setDraggedOverColumnId(null);
                  }
                }
              }}
              onDrop={(e) => {
                if (draggedColumnId) {
                  handleColumnDrop(e, column.id);
                } else {
                  handleDrop(column.id, e);
                }
              }}
            >
              {/* Header */}
              <div className="bg-white px-3 py-3 border-b flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {editingColumnId === column.id ? (
                    <div className="flex items-center gap-1 flex-1 min-w-0">
                      <input
                        autoFocus
                        value={editingColumnName}
                        onChange={(e) => setEditingColumnName(e.target.value.toUpperCase())}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void handleSaveColumnName(column.id);
                          if (e.key === 'Escape') {
                            setEditingColumnId(null);
                            setEditingColumnName('');
                          }
                        }}
                        className="flex-1 min-w-0 text-xs font-semibold tracking-wide uppercase bg-transparent border-b border-blue-400 focus:outline-none text-gray-900 px-1 py-0.5"
                      />
                      <button
                        type="button"
                        onClick={() => void handleSaveColumnName(column.id)}
                        disabled={isSavingColumnName}
                        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex-shrink-0"
                      >
                        {isSavingColumnName ? (
                          <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block" />
                        ) : (
                          <Check className="w-3 h-3" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setEditingColumnId(null); setEditingColumnName(''); }}
                        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 flex-shrink-0"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wide truncate">{column.name}</h3>
                      <span className="bg-gray-200 text-gray-700 text-xs font-medium px-2 py-1 rounded-full flex-shrink-0">
                        {(tasksByStatusId[column.id] || []).length}
                      </span>
                    </>
                  )}
                </div>

                {editingColumnId !== column.id && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditingColumnId(column.id);
                          setEditingColumnName(column.name);
                        }}
                        className="w-7 h-7 inline-flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                        title="Edit status name"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {onInlineTaskCreate && !isViewerOnly && (
                      <button
                        type="button"
                        onClick={() => toggleComposer(column.id)}
                        className="w-7 h-7 inline-flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                        title="Quick add task"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Body - Expands automatically when tasks are added via drag & drop */}
              <ColumnDroppable
                id={column.id}
                isDraggedOver={isDraggedOver}
                onDragOver={(e: any) => handleDragOver(e, column.id)}
                onDrop={(e: any) => handleDrop(column.id, e)}
              >
                {renderComposer(column.id)}

                <SortableContext items={(activeBoardTasks[column.id] || []).map(t => t.id)} strategy={verticalListSortingStrategy}>
                  {(activeBoardTasks[column.id] || []).map((task) => (
                    <SortableTaskWrapper key={task.id} task={task}>
                      <TaskCard
                        task={task}
                        onClick={() => onTaskClick(task)}
                        onUpdate={onTaskUpdate}
                        onDelete={onTaskUpdate}
                        project={projects.find(p => p.id === task.project_id)}
                        projectMembers={projectMembers}
                        taskTypes={taskTypes}
                        priorities={priorities}
                        isViewerOnly={isViewerOnly}
                      />
                    </SortableTaskWrapper>
                  ))}
                </SortableContext>

                {(activeBoardTasks[column.id] || []).length === 0 && (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    No tasks
                  </div>
                )}

                {/* Extra space visible when dragging over this column */}
                {isDraggedOver && (
                  <div className="mt-2 min-h-[60px] border-2 border-dashed border-blue-400 rounded-lg bg-blue-50 flex items-center justify-center">
                    <span className="text-blue-600 text-sm font-medium">Drop here</span>
                  </div>
                )}
              </ColumnDroppable>
            </div>
          );
        })}

        {/* Add Status Column */}
        {!isViewerOnly && onAddStatus &&isAdmin&&(
          <div className="w-80 flex-shrink-0 flex flex-col pt-0">
            {!isAddingStatus ? (
              <button
                onClick={() => setIsAddingStatus(true)}
                className="flex items-center justify-center gap-2 text-gray-500 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-lg h-12 px-4 border border-dashed border-gray-300 transition-colors w-full"
              >
                <Plus className="w-5 h-5" />
                <span className="font-medium text-sm">Add Status</span>
              </button>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 relative flex flex-col gap-3 transition-all h-max w-full">
                <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                  <span className="text-sm font-semibold text-gray-700">New Status</span>
                  <button onClick={() => setIsAddingStatus(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Status Form Matches CreateProjectModal UI */}
                <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-blue-400 bg-blue-50/30">
                  <div className="relative flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowColorPicker(!showColorPicker)}
                      className="w-6 h-6 rounded-full border-2 border-white shadow ring-1 ring-gray-200 hover:ring-gray-400 transition-all flex-shrink-0"
                      style={{ backgroundColor: newStatusParams.color }}
                      title="Pick color"
                    />
                    {showColorPicker && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowColorPicker(false)} />
                        <div className="absolute top-8 left-0 z-20 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-56">
                          <p className="text-xs text-gray-400 mb-2">Quick picks</p>
                          <div className="grid grid-cols-6 gap-1.5 mb-3">
                            {COLOR_SWATCHES.map((c) => (
                              <button
                                key={c}
                                type="button"
                                onClick={() => {
                                  setNewStatusParams(p => ({ ...p, color: c }));
                                  setShowColorPicker(false);
                                }}
                                className={`w-6 h-6 rounded-md hover:scale-110 transition-transform ring-offset-1 ${newStatusParams.color === c ? 'ring-2 ring-blue-500' : 'hover:ring-1 hover:ring-gray-300'}`}
                                style={{ backgroundColor: c }}
                                title={c}
                              />
                            ))}
                          </div>
                          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                            <div className="w-4 h-4 rounded-full flex-shrink-0 border border-gray-200" style={{ backgroundColor: newStatusParams.color }} />
                            <input
                              type="text"
                              value={newStatusParams.color}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) setNewStatusParams(p => ({ ...p, color: v }));
                              }}
                              className="flex-1 text-xs font-mono bg-transparent focus:outline-none text-gray-700 min-w-0"
                              placeholder="#000000"
                              maxLength={7}
                            />
                            <button
                              type="button"
                              onClick={() => setShowColorPicker(false)}
                              className="text-xs text-blue-600 font-medium hover:text-blue-700 flex-shrink-0"
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <input
                    type="text"
                    value={newStatusParams.name}
                    onChange={(e) => setNewStatusParams(p => ({ ...p, name: e.target.value.toUpperCase() }))}
                    placeholder="STATUS NAME"
                    autoFocus
                    className="flex-1 min-w-0 px-2 py-0.5 text-xs font-semibold tracking-wide bg-transparent border-b border-transparent focus:border-blue-400 focus:outline-none transition-colors text-gray-800 placeholder-gray-400"
                  />
                  <input
                    type="text"
                    value={newStatusParams.category}
                    onChange={(e) => setNewStatusParams(p => ({ ...p, category: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                    placeholder="CATEGORY *"
                    className={`w-24 px-2 py-0.5 text-xs bg-transparent border-b focus:outline-none transition-colors ${!newStatusParams.category.trim() ? 'border-red-300 focus:border-red-400 text-gray-800 placeholder-red-300' : 'border-transparent focus:border-blue-400 text-gray-400 placeholder-gray-400'}`}
                  />
                </div>
                
                <div className="flex justify-end gap-2 mt-1">
                  <button onClick={() => setIsAddingStatus(false)} className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors">
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!newStatusParams.name.trim() || !newStatusParams.category.trim()) return;
                      setIsSubmittingStatus(true);
                      try {
                        await onAddStatus({ ...newStatusParams });
                        await fetchStatuses();
                        setIsAddingStatus(false);
                        setNewStatusParams({ name: '', color: '#6B7280', category: '' });
                        toast.success('Status created successfully');
                      } catch (e:any) {
                        const message = e?.response?.data?.message || e?.message || 'Failed to create status';
                        toast.error(message);
                      } finally {
                        setIsSubmittingStatus(false);
                      }
                    }}
                    disabled={isSubmittingStatus || !newStatusParams.name.trim() || !newStatusParams.category.trim()}
                    className="px-4 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmittingStatus && <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block" />}
                    Save Status
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      </DndContext>
    </div>
  );
};

